<template>
    <Container height="with-footer" class="place-items-center grid">
        <Head>
            <Title>{{ $t("verify.verify.pageTitle") }}</Title>
            <Meta name="description" :content="$t('verify.verify.metaDescription')" />
        </Head>
        <div class="mx-auto my-16 w-full mt-(--header-height)">
            <WidgetSectionTitle
                class="mb-8 text-4xl text-center"
                :title="$t('verify.verify.section.title')"
                :sub-title="$t('verify.verify.section.subtitle')"
                tag="h1"
            />
            <Transition name="slide-fade" mode="out-in">
                <div
                    v-if="showInput"
                    :style="bannerStyle"
                    class="flex items-center gap-4 mx-auto mt-8 rounded-2xl w-full max-w-xl min-h-80 -translate-x-1/12 bg-cover bg-center"
                >
                    <Glass class="p-6 w-3/4 max-w-lg translate-x-1/6 translate-y-full">
                        <Form :model="form" class="w-full" @submit="onSubmit">
                            <div class="flex flex-col gap-4">
                                <FormField name="code" :label="$t('verify.verify.input.label')" class="mb-4 w-full">
                                    <Input
                                        v-model="form.code"
                                        autofocus
                                        type="text"
                                        :placeholder="$t('verify.verify.input.placeholder')"
                                        class="w-full placeholder:text-white placeholder:text-center"
                                        autocomplete="off"
                                    />
                                </FormField>
                                <Button type="submit" class="text-white">{{ $t("verify.verify.button.verify") }}</Button>
                            </div>
                        </Form>
                    </Glass>
                </div>
                <Loading v-else-if="status === 'pending'" />
                <div v-else-if="error || data?.error">
                    <div class="flex flex-col justify-center items-center">
                        <Icon name="mdi:alert-circle-outline" class="mb-4 size-12! text-destructive" />
                        <h2 class="mb-2 font-semibold text-destructive text-2xl">{{ $t("verify.verify.validation.failedTitle") }}</h2>
                        <p class="mb-4 text-destructive text-lg" v-html="$t('verify.verify.validation.failedMessage')"></p>
                        <Button variant="destructive" @click="showInput = true">{{ $t("verify.verify.button.tryAgain") }}</Button>
                    </div>
                </div>
                <div v-else-if="data" class="mx-auto max-w-5xl">
                    <WidgetSectionTitle class="text-2xl" :title="$t('verify.verify.info.title')" tag="h2" />
                    <div class="flex max-lg:flex-col justify-center items-center gap-12 w-full">
                        <div v-if="data?.image" class="w-1/2 aspect-square">
                            <NuxtImg
                                :src="proxied(`https://my.abgineh.com/${data?.image}`)"
                                alt="Product image"
                                format="webp"
                                quality="80"
                                class="w-full h-full object-contain"
                            />
                        </div>
                        <div class="flex max-lg:flex-col justify-between gap-8 mt-8" :class="{ 'flex-col': data?.image }">
                            <div v-for="(item, key) in info" :key class="flex items-center gap-4">
                                <PageProductDetailCard :image="item.image" class="max-w-24" />
                                <div>
                                    <h6 class="opacity-75 text-xs">{{ item.title }}</h6>
                                    <p class="font-bold text-2xl">{{ item.value }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="data?.stones?.length">
                        <Separator class="my-8" />
                        <WidgetSectionTitle class="text-2xl" :title="$t('verify.verify.stones.title')" tag="h2" />
                        <ul class="justify-center gap-x-10 gap-y-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mt-8">
                            <li
                                v-for="(item, key) in data.stones"
                                :key
                                class="after:top-1/12 after:-left-5 after:absolute relative flex items-start gap-8 after:bg-gray-300 pb-4 nth-[-n+3]:border-b after:w-px after:h-5/6 nth-[3n-2]:after:h-0"
                            >
                                <div class="relative w-1/3">
                                    <PageProductDetailCard :image="getStoneShapeImage(item.StoneShape)" />
                                    <p
                                        class="right-0 bottom-0 box-content absolute flex justify-center items-center bg-white/30 shadow backdrop-blur-[1px] p-1 w-1/3 aspect-square translate-x-1/2 translate-y-1/2"
                                    >
                                        <b class="text-lg">x</b>
                                        <span class="font-bold text-2xl">
                                            {{ item.StoneCount }}
                                        </span>
                                    </p>
                                </div>
                                <div class="w-2/3">
                                    <p class="flex items-center gap-2 mt-2">
                                        <span class="opacity-75 w-1/3 text-xs">{{ $t("verify.stone.name") }}</span>
                                        <span class="w-2/3 font-bold">{{ item.StoneName }}</span>
                                    </p>
                                    <p class="flex items-center gap-2 mt-2">
                                        <span class="opacity-75 w-1/3 text-xs">{{ $t("verify.stone.size") }}</span>
                                        <span class="w-2/3 font-bold">{{ item.StoneSize }}</span>
                                    </p>
                                    <p class="flex items-center gap-2 mt-2">
                                        <span class="opacity-75 w-1/3 text-xs">{{ $t("verify.stone.shape") }}</span>
                                        <span class="w-2/3 font-bold">{{ item.StoneShape }}</span>
                                    </p>
                                    <p class="flex items-center gap-2 mt-2">
                                        <span class="opacity-75 w-1/3 text-xs">{{ $t("verify.stone.milling") }}</span>
                                        <span class="w-2/3 font-bold">{{ item.StoneMilling }}</span>
                                    </p>
                                    <p class="flex items-center gap-2 mt-2">
                                        <span class="opacity-75 w-1/3 text-xs">{{ $t("verify.stone.weight") }}</span>
                                        <span class="w-2/3 font-bold">{{ item.StoneWeight }}g</span>
                                    </p>
                                </div>
                            </li>
                        </ul>
                    </div>
                    <div class="flex justify-center mt-24">
                        <Button class="text-white" @click="clearInfo">
                            <Icon name="majesticons:reload-line" />
                            <span>{{ $t("verify.verify.button.reload") }}</span>
                        </Button>
                    </div>
                </div>
            </Transition>
        </div>
    </Container>
</template>

<script lang="ts" setup>
import { Button } from "~/components/ui/button";
import { Form, FormField } from "~/components/ui/form";
import Glass from "~/components/ui/glass/Glass.vue";
import { Input } from "~/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useI18n } from "vue-i18n";

const route = useRoute();

const form = reactive({
    code: "",
});

const { t } = useI18n();

const codeFromQuery = computed(() => {
    const code = route.query.code;

    if (Array.isArray(code)) {
        return code[0] ?? "";
    }

    return typeof code === "string" ? code : "";
});

watch(
    codeFromQuery,
    (value) => {
        if (value && form.code !== value) {
            form.code = value;
        }
    },
    { immediate: true },
);

onMounted(() => {
    if (codeFromQuery.value) {
        onSubmit();
    }
});

// Show the result on submit
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const data = ref<any>(null);
const status = ref<string | null>(null);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const error = ref<any>(null);

// Show input or result
const showInput = shallowRef(true);

async function onSubmit() {
    status.value = "pending";
    error.value = null;
    data.value = null;
    showInput.value = false;
    try {
        const result = await $fetch(`/api/verify?code=${form.code}`);
        data.value = result;
        status.value = "success";
    } catch (err) {
        error.value = err;
        status.value = "error";
    }
}

function clearInfo() {
    data.value = null;
    form.code = "";
    showInput.value = true;
}

const info = computed(() => {
    const items = [
        { title: t("verify.info.codingType"), value: data.value?.type, image: getProductTypeImage(data.value?.type) },
        { title: t("verify.info.goldNetWeight"), value: `${data.value?.net_weight}g`, image: "/images/sketches/scale.png" },
        { title: t("verify.info.metalColorName"), value: data.value?.color, image: "/images/sketches/colors.png" },
        {
            title: t("verify.info.totalStoneCount"),
            value: `${data.value?.stone_count} ${t("verify.info.pieces")}`,
            image: "/images/sketches/counter.png",
        },
    ];

    // Return only items that have a defined, non-null and non-empty value
    return items.filter((it) => it.value !== undefined && it.value !== null && it.value !== "");
});

const proxied = (u: string) => {
    // Build absolute URL so IPX treats it as external and doesn't forbid internal path
    if (import.meta.client) {
        const origin = window.location.origin;
        return `${origin}/api/image-proxy?url=${encodeURIComponent(u)}`;
    }
    const runtime = useRuntimeConfig();
    const base = runtime.public.appUrl || "";
    if (base) return `${base.replace(/\/$/, "")}/api/image-proxy?url=${encodeURIComponent(u)}`;
    return `/api/image-proxy?url=${encodeURIComponent(u)}`;
};

const { data: banner } = useBanners("gallery");

const bannerStyle = computed(() => {
    if (!banner.value.data[0]?.media)
        return {
            background: "oklch(0.9364 0.0273 93.1105)",
        };
    return {
        "background-image": `url('${getSingleImage(banner.value.data[0]?.media)?.src}')`,
    };
});
</script>

<style scoped>
.slide-fade-enter-active {
    transition: all 0.2s ease-out;
}

.slide-fade-leave-active {
    transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
    transform: translateY(15px);
    opacity: 0;
}
</style>
